import { useEffect, useState } from 'react';

import { fetch_month_runs } from '../../api';
import { run_summary_view } from './adapters';
import { is_run_active, poll_delay_ms } from './params';

/*
 * PROD-7828 D110 — the month's active run for a page that only needs its progress (the Overview
 * banner): GET /month/runs on mount, then again at the run panel's rate while a run is active, one
 * request at a time; it stops when no run is active and on unmount. A failed first read hides the
 * banner (decoration, like the tab badges); a failed re-read keeps it and tries again.
 * Coming back to the page re-reads at once rather than waiting out the hidden-page delay.
 */
const is_hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

const useActiveRun = (pay_month) => {
  const [run, setRun] = useState(null);

  useEffect(() => {
    let stopped = false;
    let timer = null;
    let in_flight = false;
    // The status of the run last seen, '' when none: it decides whether and how soon to read again.
    let status = '';
    setRun(null);
    if (!pay_month) return undefined;

    const schedule = () => {
      clearTimeout(timer);
      if (!stopped && is_run_active(status)) {
        // eslint-disable-next-line no-use-before-define
        timer = setTimeout(read, poll_delay_ms(is_hidden(), status));
      }
    };
    const read = () => {
      in_flight = true;
      fetch_month_runs(pay_month)
        .then((data) => {
          if (stopped) return;
          const active =
            data && data.active && is_run_active(data.active.status) ? data.active : null;
          status = active ? active.status : '';
          setRun(active ? run_summary_view(active) : null);
        })
        .catch(() => {
          if (!stopped && !status) setRun(null);
        })
        .finally(() => {
          in_flight = false;
          schedule();
        });
    };
    const on_visibility = () => {
      if (!is_hidden() && is_run_active(status) && !in_flight) {
        clearTimeout(timer);
        read();
      }
    };

    read();
    document.addEventListener('visibilitychange', on_visibility);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', on_visibility);
    };
  }, [pay_month]);

  return run;
};

export default useActiveRun;
